import { NgModule, ErrorHandler, Injectable } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { DatabaseService } from './services/database.service';
import { StorageService } from './services/storage.service';
import { ObserverService } from './services/observer.service';
import { AuthService } from './services/auth.service';
import { CartService } from './services/cart.service';
import { ProductService } from './services/product.service';
import { PersonService } from './services/person.service';
import { EventService } from './services/event.service';
import { RegisterService } from './services/register.service';
import { UserService } from './services/user.service';
import { PricingTierService } from './services/pricing-tier.service';
import { OrganisationService } from './services/organisation.service';
import { TaxZoneService } from './services/tax-zone.service';
import { SupportedCurrencyService } from './services/supported-currency.service';
import { ConnectedAccountService } from './services/connected-account.service';
import { GlobalVariableService } from './services/globalvariable.service';
import { APIService } from './services/api.service';
import { PusherService } from './services/pusher.service';
import { PendingOrderService } from './services/pending-order.service';
import { CountryService } from './services/country.service';
import { CourierService } from './services/courier.service';
import { DonationCampaignService } from './services/donation-campaign.service';
import { GiftVoucherService } from './services/gift-voucher.service';
import { CashTrackingService } from './services/cash-tracking.service';
import { ParkedCartService} from './services/parked-cart.service';
import { OrderService } from './services/order.service';
import { CustomPaymentServiceService } from './services/custom-payment-service.service';
import { MeasurementUnitService } from './services/measurement-unit.service';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor';
import { FormsModule } from '@angular/forms';
import { Printer } from '@capawesome-team/capacitor-printer';
import { RouterExtService } from './services/routerext.service';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { UserPINVerifyPageModule } from './modals/user-pin-verify/user-pin-verify.module';
import { UserPINChangePageModule } from './modals/user-pin-change/user-pin-change.module';
import { CartAdditionalInfoPageModule } from './modals/cart-additional-info/cart-additional-info.module';
import { CartPickupPageModule } from './modals/cart-pickup/cart-pickup.module';
import { CartShippingPageModule } from './modals/cart-shipping/cart-shipping.module';
import { CartCustomerPageModule } from './modals/cart-customer/cart-customer.module';
import { CartOptionsComponent } from './popovers/cart-options/cart-options.component';
import { CartPricingTierPageModule } from './modals/cart-pricing-tier/cart-pricing-tier.module';
import { CartCurrencyPageModule } from './modals/cart-currency/cart-currency.module';
import { CartParkPageModule} from './modals/cart-park/cart-park.module';
import { CartDiscountPageModule} from './modals/cart-discount/cart-discount.module';
import { CartEditItemPageModule } from './modals/cart-edit-item/cart-edit-item.module';
import { OrderOptionsComponent } from './popovers/order-options/order-options.component';
import { OrderPaymentOptionsComponent } from './popovers/order-payment-options/order-payment-options.component';
import { OrderRefundOptionsComponent } from './popovers/order-refund-options/order-refund-options.component';
import { OrderRefundPaymentOptionsComponent } from './popovers/order-refund-payment-options/order-refund-payment-options.component';
import { OrderReturnOptionsComponent } from './popovers/order-return-options/order-return-options.component';
import { OrderFulfillmentOptionsComponent } from './popovers/order-fulfillment-options/order-fulfillment-options.component';
import { OrderConfirmationPrintOptionsComponent } from './popovers/order-confirmation-print-options/order-confirmation-print-options.component';
import { OrdersQuickSearchComponent } from './popovers/orders-quick-search/orders-quick-search.component';
import { PersonOptionsComponent } from './popovers/person-options/person-options.component';
import { PersonShippingAddressOptionsComponent } from './popovers/person-shipping-address-options/person-shipping-address-options.component';
import { ParkedCartOptionsComponent } from './popovers/parked-cart-options/parked-cart-options.component';
import { OrganisationOptionsComponent } from './popovers/organisation-options/organisation-options.component';
import { OrganisationShippingAddressOptionsComponent } from './popovers/organisation-shipping-address-options/organisation-shipping-address-options.component';
import { PersonEditPageModule } from './modals/person-edit/person-edit.module';
import { PersonShippingAddressAddPageModule } from './modals/person-shipping-address-add/person-shipping-address-add.module';
import { PersonShippingAddressEditPageModule } from './modals/person-shipping-address-edit/person-shipping-address-edit.module';
import { PersonAddPageModule } from './modals/person-add/person-add.module';
import { PersonAddCommentPageModule } from './modals/person-add-comment/person-add-comment.module';
import { PersonFilterPageModule } from './modals/person-filter/person-filter.module'
import { SelectSubProductPageModule } from './modals/select-sub-product/select-sub-product.module';
import { CheckoutPaymentExternalCardPageModule } from './modals/checkout-payment-external-card/checkout-payment-external-card.module';
import { CheckoutPaymentCashPageModule } from './modals/checkout-payment-cash/checkout-payment-cash.module';
import { CheckoutPaymentChequePageModule } from './modals/checkout-payment-cheque/checkout-payment-cheque.module';
import { CheckoutPaymentGiftVoucherPageModule } from './modals/checkout-payment-gift-voucher/checkout-payment-gift-voucher.module';
import { CheckoutPaymentStoreCreditPageModule } from './modals/checkout-payment-store-credit/checkout-payment-store-credit.module';
import { CheckoutPaymentOnAccountPageModule } from './modals/checkout-payment-on-account/checkout-payment-on-account.module';
import { CheckoutPaymentBankTransferPageModule } from './modals/checkout-payment-bank-transfer/checkout-payment-bank-transfer.module';
import { CheckoutPaymentStripeTerminalPageModule } from './modals/checkout-payment-stripe-terminal/checkout-payment-stripe-terminal.module';
import { CheckoutPaymentCustomPaymentServicePageModule } from './modals/checkout-payment-custom-payment-service/checkout-payment-custom-payment-service.module';
import { SelectEventTicketTypePageModule } from './modals/select-event-ticket-type/select-event-ticket-type.module';
import { SelectEventSessionPageModule } from './modals/select-event-session/select-event-session.module';
import { SelectEventSessionTicketTypePageModule } from './modals/select-event-session-ticket-type/select-event-session-ticket-type.module';
import { SettingsStarMicronicsReceiptPrinterPageModule } from './modals/settings-star-micronics-receipt-printer/settings-star-micronics-receipt-printer.module';
import { SettingsStarMicronicsReceiptPrinterFinderPageModule } from './modals/settings-star-micronics-receipt-printer-finder/settings-star-micronics-receipt-printer-finder.module';
import { SettingsChangeRegisterPageModule } from './modals/settings-change-register/settings-change-register.module';
import { SettingsChangeSmartpayReaderPageModule } from './modals/settings-change-smartpay-reader/settings-change-smartpay-reader.module';
import { SettingsChangeStripeTerminalReaderPageModule } from './modals/settings-change-stripe-terminal-reader/settings-change-stripe-terminal-reader.module';
import { SellDonationPageModule } from './modals/sell-donation/sell-donation.module';
import { SellGiftVoucherPageModule } from './modals/sell-gift-voucher/sell-gift-voucher.module';
import { SettingsChangePrinterPaperSizesPageModule } from './modals/settings-change-printer-paper-sizes/settings-change-printer-paper-sizes.module';
import { CashTrackingStartTrackingPageModule} from './modals/cash-tracking-start-tracking/cash-tracking-start-tracking.module';
import { CashTrackingEndTrackingPageModule} from './modals/cash-tracking-end-tracking/cash-tracking-end-tracking.module';
import { CashTrackingAddCashPageModule} from './modals/cash-tracking-add-cash/cash-tracking-add-cash.module';
import { CashTrackingRemoveCashPageModule} from './modals/cash-tracking-remove-cash/cash-tracking-remove-cash.module';
import { CashTrackingCashMovementsPageModule } from './modals/cash-tracking-cash-movements/cash-tracking-cash-movements.module';
import { ParkedCartEmailPageModule } from './modals/parked-cart-email/parked-cart-email.module';
import { ParkedCartAdditionalInfoPageModule } from './modals/parked-cart-additional-info/parked-cart-additional-info.module';
import { ReportsPendingOrdersPageModule } from './modals/reports-pending-orders/reports-pending-orders.module';
import { OrderFilterPageModule } from './modals/order-filter/order-filter.module'
import { OrderEmailInvoicePageModule } from './modals/order-email-invoice/order-email-invoice.module'
import { OrderAddCommentPageModule } from './modals/order-add-comment/order-add-comment.module'
import { OrderEditPageModule } from './modals/order-edit/order-edit.module'
import { OrderItemEditPageModule } from './modals/order-item-edit/order-item-edit.module'
import { PersonPickerPageModule } from './modals/person-picker/person-picker.module';
import { OrganisationPickerPageModule } from './modals/organisation-picker/organisation-picker.module';
import { OrganisationAddPageModule } from './modals/organisation-add/organisation-add.module';
import { OrganisationEditPageModule } from './modals/organisation-edit/organisation-edit.module';
import { OrganisationShippingAddressAddPageModule } from './modals/organisation-shipping-address-add/organisation-shipping-address-add.module';
import { OrganisationShippingAddressEditPageModule } from './modals/organisation-shipping-address-edit/organisation-shipping-address-edit.module';
import { OrganisationAddCommentPageModule } from './modals/organisation-add-comment/organisation-add-comment.module';
import { OrganisationFilterPageModule } from './modals/organisation-filter/organisation-filter.module'
import { CountryPickerPageModule } from './modals/country-picker/country-picker.module';
import { AffiliatePickerPageModule } from './modals/affiliate-picker/affiliate-picker.module';
import { AddressPickerPageModule } from './modals/address-picker/address-picker.module';
import { DateTimePickerPageModule } from './modals/date-time-picker/date-time-picker.module';
import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { StarPRNT } from '@awesome-cordova-plugins/star-prnt/ngx';
import * as Sentry from "@sentry/capacitor";
import * as SentryAngular from "@sentry/angular";
import { OrderPaymentAddPageModule } from './modals/order-payment-add/order-payment-add.module';
import { OrderPaymentEditPageModule } from './modals/order-payment-edit/order-payment-edit.module';
import { OrderRefundAddPageModule } from './modals/order-refund-add/order-refund-add.module';
import { OrderRefundEditPageModule } from './modals/order-refund-edit/order-refund-edit.module';
import { OrderRefundPaymentAddPageModule } from './modals/order-refund-payment-add/order-refund-payment-add.module';
import { OrderRefundPaymentEditPageModule } from './modals/order-refund-payment-edit/order-refund-payment-edit.module';
import { OrderReturnAddPageModule } from './modals/order-return-add/order-return-add.module';
import { OrderReturnEditPageModule } from './modals/order-return-edit/order-return-edit.module';
import { OrderFulfillmentEditPageModule } from './modals/order-fulfillment-edit/order-fulfillment-edit.module';
import { OrderFulfillmentAddPageModule } from './modals/order-fulfillment-add/order-fulfillment-add.module';
import { StripeTerminalReaderService } from './services/stripe-terminal-reader.service';
import { ReportsPaymentsAndRefundSearchPageModule } from './modals/reports-payments-and-refunds-search/reports-payments-and-refunds-search.module';
import { CheckoutPaymentSmartpayCardPageModule } from './modals/checkout-payment-smartpay/checkout-payment-smartpay.module';
import { ShippingAddressAddPageModule } from './modals/shipping-address-add/shipping-address-add.module';

Sentry.init(
    {
        dsn: 'https://b7dd747783c74d2f9633a92f7c9ccb6a@o292210.ingest.sentry.io/5544150',
        release: 'pos@2.8.5',
        enabled: process.env.NODE_ENV === 'production'
    },
    SentryAngular.init
);

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

    handleError(error: any): void {

        const chunkFailedMessage = /Loading chunk [^\s]+ failed/;
        
        if(chunkFailedMessage.test(error.message)) {
            window.location.reload();
        }

        console.error(error);

    }

}

@NgModule({
    declarations: [
        AppComponent,
        CartOptionsComponent,
        OrderOptionsComponent,
        OrderPaymentOptionsComponent,
        OrderRefundOptionsComponent,
        OrderRefundPaymentOptionsComponent,
        OrderReturnOptionsComponent,
        OrderFulfillmentOptionsComponent,
        OrderConfirmationPrintOptionsComponent,
        OrdersQuickSearchComponent,
        PersonOptionsComponent,
        PersonShippingAddressOptionsComponent,
        ParkedCartOptionsComponent,
        OrganisationOptionsComponent,
        OrganisationShippingAddressOptionsComponent,
    ],
    imports: [
        IonicModule.forRoot({
            innerHTMLTemplatesEnabled: true
        }),
        FontAwesomeModule,
        BrowserModule,
        AppRoutingModule,
        HttpClientModule,
        FormsModule,
        UserPINVerifyPageModule,
        UserPINChangePageModule,
        CartAdditionalInfoPageModule,
        CartDiscountPageModule,
        CartPickupPageModule,
        CartShippingPageModule,
        CartCustomerPageModule,
        CartEditItemPageModule,
        CartPricingTierPageModule,
        CartCurrencyPageModule,
        CartParkPageModule,
        SettingsChangeRegisterPageModule,
        SettingsChangeSmartpayReaderPageModule,
        SettingsChangeStripeTerminalReaderPageModule,
        SettingsChangeSmartpayReaderPageModule,
        PersonEditPageModule,
        PersonShippingAddressAddPageModule,
        PersonShippingAddressEditPageModule,
        PersonAddPageModule,
        PersonAddCommentPageModule,
        SelectSubProductPageModule,
        CheckoutPaymentExternalCardPageModule,
        CheckoutPaymentCashPageModule,
        CheckoutPaymentChequePageModule,
        CheckoutPaymentGiftVoucherPageModule,
        CheckoutPaymentStoreCreditPageModule,
        CheckoutPaymentOnAccountPageModule,
        CheckoutPaymentBankTransferPageModule,
        CheckoutPaymentStripeTerminalPageModule,
        CheckoutPaymentSmartpayCardPageModule,
        CheckoutPaymentCustomPaymentServicePageModule,
        SelectEventTicketTypePageModule,
        SelectEventSessionPageModule,
        SelectEventSessionTicketTypePageModule,
        SettingsStarMicronicsReceiptPrinterPageModule,
        SettingsStarMicronicsReceiptPrinterFinderPageModule,
        SellDonationPageModule,
        SellGiftVoucherPageModule,
        SettingsChangePrinterPaperSizesPageModule,
        ReportsPaymentsAndRefundSearchPageModule,
        ReportsPendingOrdersPageModule,
        CashTrackingStartTrackingPageModule,
        CashTrackingEndTrackingPageModule,
        CashTrackingAddCashPageModule,
        CashTrackingRemoveCashPageModule,
        CashTrackingCashMovementsPageModule,
        ParkedCartEmailPageModule,
        ParkedCartAdditionalInfoPageModule,
        OrderFilterPageModule,
        OrderEmailInvoicePageModule,
        OrderAddCommentPageModule,
        OrderPaymentAddPageModule,
        OrderPaymentEditPageModule,
        OrderRefundAddPageModule,
        OrderRefundEditPageModule,
        OrderRefundPaymentAddPageModule,
        OrderRefundPaymentEditPageModule,
        OrderReturnAddPageModule,
        OrderReturnEditPageModule,
        OrderFulfillmentEditPageModule,
        OrderFulfillmentAddPageModule,
        OrderEditPageModule,
        OrderItemEditPageModule,
        PersonPickerPageModule,
        PersonFilterPageModule,
        OrganisationPickerPageModule,
        OrganisationFilterPageModule,
        VirtualScrollerModule,
        OrganisationAddPageModule,
        OrganisationEditPageModule,
        OrganisationShippingAddressAddPageModule,
        OrganisationShippingAddressEditPageModule,
        OrganisationAddCommentPageModule,
        CountryPickerPageModule,
        AffiliatePickerPageModule,
        AddressPickerPageModule,
        DateTimePickerPageModule,
        ShippingAddressAddPageModule
    ],
    providers: [
        StorageService,
        CartService,
        DatabaseService,
        PersonService,
        ProductService,
        EventService,
        ObserverService,
        APIService,
        PusherService,
        AuthService,
        PricingTierService,
        RegisterService,
        UserService,
        OrganisationService,
        TaxZoneService,
        SupportedCurrencyService,
        ConnectedAccountService,
        PendingOrderService,
        CountryService,
        CourierService,
        DonationCampaignService,
        GiftVoucherService,
        CashTrackingService,
        ParkedCartService,
        OrderService,
        CustomPaymentServiceService,
        MeasurementUnitService,
        GlobalVariableService,
        StripeTerminalReaderService,
        RouterExtService,
        StarPRNT,
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true
        },
        { provide: ErrorHandler, useValue: SentryAngular.createErrorHandler() }
    ],
    bootstrap: [AppComponent]
})

export class AppModule {
    constructor(library: FaIconLibrary) {
        library.addIconPacks(fas);
        library.addIconPacks(far);
        library.addIconPacks(fab);
        library.addIconPacks(fal);
    }
}
